import { ref, computed } from 'vue';

/**
 * Multiple reputation tier set definitions
 */
export const reputationTierSets = {
  "Standard": {
    name: "Standard",
    tiers: [
      {
        name: "Bronze",
        symbol: "🥉",
        position: 1,
        description: "Beginner",
        percentileRange: [0, 34]
      },
      {
        name: "Silver",
        symbol: "🥈",
        position: 2,
        description: "Okay",
        percentileRange: [35, 64]
      },
      {
        name: "Gold",
        symbol: "🥇",
        position: 3,
        description: "Good",
        percentileRange: [65, 79]
      },
      {
        name: "Ruby",
        symbol: "🔻",
        position: 4,
        description: "Great",
        percentileRange: [80, 94]
      },
      {
        name: "Diamond",
        symbol: "💎",
        position: 5,
        description: "Expert",
        percentileRange: [95, 100]
      }
    ]
  },
  "Mythical Creatures": {
    name: "Mythical Creatures",
    tiers: [
      {
        name: "Goblin",
        symbol: "👺",
        position: 1,
        description: "Small but fierce, growing in power",
        percentileRange: [0, 34]
      },
      {
        name: "Orc",
        symbol: "👹",
        position: 2,
        description: "Brave and regal, rising to greatness",
        percentileRange: [35, 64]
      },
      {
        name: "Centaur",
        symbol: "🐎",
        position: 3,
        description: "Strong and wise, versatile adventurer",
        percentileRange: [65, 79]
      },
      {
        name: "Dragon",
        symbol: "🐉",
        position: 4,
        description: "Dominant and skilled, a force to fear",
        percentileRange: [80, 94]
      },
      {
        name: "Unicorn",
        symbol: "🦄",
        position: 5,
        description: "Ultimate status—reborn and unstoppable",
        percentileRange: [95, 100]
      }
    ]
  },
  "Fish": {
    name: "Fish",
    tiers: [
      {
        name: "Minnow",
        symbol: "🐟",
        position: 1,
        description: "Small and new, but full of potential",
        percentileRange: [0, 34]
      },
      {
        name: "Clownfish",
        symbol: "🐠",
        position: 2,
        description: "Vibrant and developing confidence",
        percentileRange: [35, 64]
      },
      {
        name: "Tuna",
        symbol: "🐟",
        position: 3,
        description: "Swift, capable, and strong",
        percentileRange: [65, 79]
      },
      {
        name: "Shark",
        symbol: "🦈",
        position: 4,
        description: "Top predator, highly skilled",
        percentileRange: [80, 94]
      },
      {
        name: "Kraken",
        symbol: "🐙",
        position: 5,
        description: "Legendary sea force, mastery achieved",
        percentileRange: [95, 100]
      }
    ]
  },
  "Animals": {
    name: "Animals",
    tiers: [
      {
        name: "Turtle",
        symbol: "🐢",
        position: 1,
        description: "Steady and learning",
        percentileRange: [0, 34]
      },
      {
        name: "Monkey",
        symbol: "🐒",
        position: 2,
        description: "Clever and curious",
        percentileRange: [35, 64]
      },
      {
        name: "Wolf",
        symbol: "🐺",
        position: 3,
        description: "Skilled and strategic",
        percentileRange: [65, 79]
      },
      {
        name: "Tiger",
        symbol: "🐯",
        position: 4,
        description: "Powerful and confident",
        percentileRange: [80, 94]
      },
      {
        name: "Dragon",
        symbol: "🐉",
        position: 5,
        description: "Legendary and unstoppable",
        percentileRange: [95, 100]
      }
    ]
  },
  "Tree": {
    name: "Tree",
    tiers: [
      {
        name: "Seedling",
        symbol: "🌱",
        position: 1,
        description: "Small start with great potential",
        percentileRange: [0, 34]
      },
      {
        name: "Sapling",
        symbol: "🌿",
        position: 2,
        description: "Growing strong with fresh leaves",
        percentileRange: [35, 64]
      },
      {
        name: "Tree",
        symbol: "🌳",
        position: 3,
        description: "Full grown and flourishing",
        percentileRange: [65, 79]
      },
      {
        name: "Oak",
        symbol: "🌲",
        position: 4,
        description: "Tall, sturdy, and well-established",
        percentileRange: [80, 94]
      },
      {
        name: "Baobab",
        symbol: "🌍",
        position: 5,
        description: "Timeless wisdom and deep roots",
        percentileRange: [95, 100]
      }
    ]
  },
  "Action Movie": {
    name: "Action Movie",
    tiers: [
      {
        name: "The Rookie",
        symbol: "🎒",
        position: 1,
        description: "Just starting the journey — brave but untested",
        percentileRange: [0, 34]
      },
      {
        name: "The Sidekick",
        symbol: "🧢",
        position: 2,
        description: "Loyal and learning — growing in confidence",
        percentileRange: [35, 64]
      },
      {
        name: "The Hero",
        symbol: "🦸",
        position: 3,
        description: "Front and center — skilled, courageous, dependable",
        percentileRange: [65, 79]
      },
      {
        name: "The Champion",
        symbol: "🛡️",
        position: 4,
        description: "Proven under pressure — respected, resourceful",
        percentileRange: [80, 94]
      },
      {
        name: "The Legend",
        symbol: "🎥",
        position: 5,
        description: "Iconic — world-saving, story-defining, unforgettable",
        percentileRange: [95, 100]
      }
    ]
  }
};

// Global state for the current tier set - always use Standard
const currentTierSetName = ref('Standard');

/**
 * Composable for managing tier set selection
 */
export function useTierSet() {
  const tierSets = computed(() => Object.values(reputationTierSets));
  const tierSetName = computed(() => currentTierSetName.value);
  const tierSet = computed(() => reputationTierSets[currentTierSetName.value]);

  const setTierSet = (name) => {
    // No-op: tier set is fixed to Standard
  };

  return {
    tierSets,
    tierSetName,
    tierSet,
    setTierSet
  };
}

// Backward compatibility - export the current tier set as reputationTiers
export const reputationTiers = computed(() => reputationTierSets[currentTierSetName.value]);

/**
 * Determines the reputation tier based on percentile score
 * @param {number} percentile - User's percentile score
 * @param {string} tierSetName - Optional tier set name to use (defaults to current)
 * @returns {object} - The tier object containing name, symbol, position, and description
 */
export function getReputationTier(percentile, tierSetName = null) {
  if (percentile === null || percentile === undefined) {
    const tierSet = tierSetName ? reputationTierSets[tierSetName] : reputationTiers.value;
    return tierSet?.tiers?.[0] || reputationTierSets[currentTierSetName.value].tiers[0]; // Default to first tier
  }

  const tierSet = tierSetName ? reputationTierSets[tierSetName] : reputationTiers.value;
  const tiers = tierSet?.tiers || reputationTierSets[currentTierSetName.value].tiers;

  // Find the appropriate tier based on percentile
  return tiers.find(tier => {
    const [min, max] = tier.percentileRange;
    return percentile >= min && percentile <= max;
  }) || tiers[0];
}
